import debug from 'debug';

// GQL
import { client } from '../client';
import { ORDERS_BY_IDS_WARMUP } from '../queries/orders/orders';

const log = debug('rois:gql:selectors:warmup');

// Límite del contrato backend: `ordersByIds` acepta máx 200 ids por llamada.
// Hoy las tablas usan take ≤ 100, el troceo es puramente defensivo.
const BATCH_SIZE = 200;

/**
 * Warm-up de la caché normalizada de Apollo para los selectors de tabla.
 *
 * Resuelve las Orders paralelas de todas las filas en UNA sola query
 * `ordersByIds` (fragment NewOrder, el MISMO de GET_ORDER_BY_ID). Las
 * entidades `Order:<id>` quedan normalizadas en caché, de modo que los
 * `client.query(GET_ORDER_BY_ID, cache-first)` per-row que vienen después
 * se sirven de caché vía el redirect `Query.order → toReference` de
 * policies.ts, en lugar de disparar 25 requests fila a fila.
 *
 * REGLA DE ORO — es una OPTIMIZACIÓN, jamás un gate: si falla (tablet
 * offline en un túnel, backend viejo sin la query `ordersByIds`, error
 * GraphQL, lo que sea) se ignora en silencio y el selector sigue
 * EXACTAMENTE como hoy — los per-row GET_ORDER_BY_ID intactos son el
 * fallback natural. Este helper NUNCA lanza.
 *
 * `fetchPolicy` (default `cache-first`, el contrato original de tablas):
 * los DETALLES (OT/campaña, sin polling ni flag reFetch que refresque
 * después) pasan `network-only` para que CADA carga del detalle baje las
 * hijas frescas en un solo round-trip — con `cache-first`, un segundo
 * visit con el MISMO set de ids se serviría de la caché del propio warm-up
 * y el detalle perdería la garantía de frescura que hoy le dan sus
 * per-row network-only. Las tablas conservan `cache-first` porque su
 * frescura la garantiza el polling con `reFetch=true` (per-row network).
 */
export async function warmupOrdersByIds(
  ids: (string | undefined | null)[],
  reFetch = false,
  fetchPolicy: 'cache-first' | 'network-only' = 'cache-first'
): Promise<void> {
  // Con reFetch=true el per-row va network-only e IGNORA la caché: el
  // warm-up solo añadiría una request extra con los mismos payloads que
  // las respuestas per-row ya traen. Solo aporta en el camino cache-first.
  if (reFetch) return;
  try {
    const uniqueIds = [
      ...new Set(ids.filter((id): id is string => Boolean(id))),
    ];
    if (uniqueIds.length === 0) return;

    const batches: string[][] = [];
    for (let i = 0; i < uniqueIds.length; i += BATCH_SIZE) {
      batches.push(uniqueIds.slice(i, i + BATCH_SIZE));
    }

    await Promise.all(
      batches.map((batch) =>
        client.query({
          query: ORDERS_BY_IDS_WARMUP,
          variables: { ids: batch },
          fetchPolicy,
          // 'all' cubre "backend desplegado sin ordersByIds" (campo
          // desconocido → error GraphQL → se ignora, no revienta).
          errorPolicy: 'all',
        })
      )
    );
  } catch (err) {
    // VACÍO de efectos a propósito: warm-up fallido = comportamiento de hoy.
    log('warm-up ordersByIds omitido (sin efectos):', err);
  }
}
